import React, { useEffect, useState, useRef } from "react";
import { useInView, motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import backgroundImage from "../../Assets/images/img16.jpg";
import "./JobDetails.scss";

const JobDetailsPage = () => {
  const [loading, setLoading] = useState(true);
  const titleRef = useRef(null);
  const isInView = useInView(titleRef, {
    once: true,
    amount: 0.1,
  });

  const { jobId } = useParams();
  const [jobDetails, setJobDetails] = useState(null);
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    user_position: "",
    user_cover: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    const fetchJobDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/jobs/${jobId}`
        );
        if (response.ok) {
          const data = await response.json();
          setJobDetails(data);
        } else {
          toast.error("Failed to fetch job details.");
        }
      } catch (error) {
        toast.error("An error occurred while fetching job details.");
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "cv") {
      setSelectedFile(files[0]);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedFile || selectedFile.type !== "application/pdf") {
      toast.error("Please upload a valid PDF file.");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("user_name", formData.user_name);
      formDataToSend.append("user_email", formData.user_email);
      formDataToSend.append("user_position", formData.user_position);
      formDataToSend.append("user_cover", formData.user_cover);
      formDataToSend.append("cv", selectedFile); // Ensure the 'cv' matches the backend expected name

      const response = await fetch(
        "https://backend.aron-engineering.com/api/submit-application",
        {
          method: "POST",
          body: formDataToSend,
          mode: "cors",
        }
      );

      if (response.ok) {
        toast.success("Application submitted successfully!");
      } else {
        const errorMessage = await response.text();
        toast.error(
          `Failed to submit application. Server response: ${errorMessage}`
        );
      }
    } catch (error) {
      toast.error("An error occurred while submitting the application.");
    }

    setSelectedFile(null);
    setFormData({
      user_name: "",
      user_email: "",
      user_position: "",
      user_cover: "",
    });
  };

  const renderTextWithNewLines = (text) => {
    if (!text) return null;

    return text.split(".").map((sentence, index) => (
      <React.Fragment key={index}>
        {sentence.trim()}
        {sentence && "."}
        <br />
      </React.Fragment>
    ));
  };

  return (
    <section className="jobdetails-page">
      <div
        className="jobdetails-banner"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="jobdetails-overlay" />
        <div className="jobdetails-text container text-left">
          <motion.div
            ref={titleRef}
            initial={{ opacity: 0, y: 100 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.99, ease: "easeOut", delay: 0.2 }}
          >
            <h5>FORMS</h5>
          </motion.div>
        </div>
      </div>

      <div className="white-container">
        <div className="inner-container"></div>
      </div>

      {loading ? (
        <div className="spinnerContainer">
          <div className="spinner"></div>
          <div className="loader">
            <p>Loading</p>
            <div className="words">
              <span className="word">requests</span>
              <span className="word">cookies</span>
              <span className="word">headers</span>
              <span className="word">caching</span>
              <span className="word">sessions</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="job-details-container">
          <div className="job-details-box">
            <div>
              <h1 className="job-title">{jobDetails.title}</h1>
              <div className="job-details-content">
                <span className="sub-title">Description :</span>
                <p>{jobDetails.description}</p>

                <span className="sub-title">Duties :</span>
                <p>{renderTextWithNewLines(jobDetails.duties)}</p>

                <span className="sub-title">Qualifications :</span>
                <p>{renderTextWithNewLines(jobDetails.qualifications)}</p>

                <span className="sub-title">Preferred Skills :</span>
                <p>{renderTextWithNewLines(jobDetails.preferredSkills)}</p>

                <span className="sub-title">Work Schedule :</span>
                <p>{jobDetails.workSchedule}</p>

                <span className="sub-title">Location :</span>

                <p>{jobDetails.location}</p>

                <span className="sub-title">Compensation and Benefits :</span>

                <p>{jobDetails.compensationAndBenefits}</p>

                <p>
                  <span className="sub-title">Expected Salary:</span> $
                  {jobDetails.expectedSalary}
                </p>
              </div>
            </div>
          </div>

          <div className="application-form" ref={formRef}>
            <h2 className="form-title">Apply for this Job</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="user_name"
                placeholder="Your Name"
                value={formData.user_name}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="user_email"
                placeholder="Your Email"
                value={formData.user_email}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="user_position"
                placeholder="Position"
                value={formData.user_position}
                onChange={handleChange}
                required
              />
              <textarea
                name="user_cover"
                placeholder="Cover Letter"
                value={formData.user_cover}
                onChange={handleChange}
                required
              ></textarea>
              <input
                type="file"
                name="cv"
                accept=".pdf"
                onChange={handleChange}
                required
              />
              <button className="submit-button" type="submit">
                Submit Application
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default JobDetailsPage;
